/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/* eslint-disable react/prop-types, jsx-a11y/anchor-has-content, jsx-a11y/heading-has-content */
// Element styles for chat bubbles (no @tailwindcss/typography in this project).
const COMPONENTS = {
  p: ({ node, ...props }) => <p className="my-1.5 first:mt-0 last:mb-0 leading-relaxed" {...props} />,
  h1: ({ node, ...props }) => <h1 className="text-base font-black mt-3 mb-1.5 first:mt-0" {...props} />,
  h2: ({ node, ...props }) => <h2 className="text-[15px] font-black mt-3 mb-1.5 first:mt-0" {...props} />,
  h3: ({ node, ...props }) => <h3 className="text-sm font-bold mt-2.5 mb-1 first:mt-0" {...props} />,
  h4: ({ node, ...props }) => <h4 className="text-sm font-bold mt-2 mb-1 first:mt-0" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-1.5 space-y-0.5" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-1.5 space-y-0.5" {...props} />,
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  a: ({ node, ...props }) => (
    <a className="link link-primary break-all" target="_blank" rel="noopener noreferrer" {...props} />
  ),
  strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-4 border-base-content/20 pl-3 my-2 text-base-content/70 italic" {...props} />
  ),
  hr: () => <hr className="my-3 border-base-content/10" />,
  pre: ({ node, ...props }) => (
    <pre className="my-2 p-3 rounded-lg bg-base-300/60 overflow-x-auto text-xs font-mono leading-relaxed" {...props} />
  ),
  code: ({ node, className, children, ...props }) => {
    // Fenced blocks arrive inside <pre> with a language class; inline code has neither.
    const isBlock = /language-/.test(className || '') || String(children).includes('\n');
    return isBlock
      ? <code className={className} {...props}>{children}</code>
      : <code className="px-1 py-0.5 rounded bg-base-300/60 font-mono text-[0.85em]" {...props}>{children}</code>;
  },
  table: ({ node, ...props }) => (
    <div className="my-2 overflow-x-auto">
      <table className="table table-xs w-full border border-base-content/10" {...props} />
    </div>
  ),
  th: ({ node, ...props }) => <th className="bg-base-300/40 font-bold text-left" {...props} />,
  td: ({ node, ...props }) => <td className="align-top" {...props} />,
  img: ({ node, alt }) => <span className="italic text-base-content/50">[image{alt ? `: ${alt}` : ''}]</span>,
};
/* eslint-enable react/prop-types, jsx-a11y/anchor-has-content, jsx-a11y/heading-has-content */

/**
 * Renders an agent reply written in Markdown (GFM: lists, tables, code, links).
 * Raw HTML in the text is not rendered (react-markdown's default), links open
 * in a new tab, and images are shown as a placeholder rather than loaded.
 */
const MarkdownMessage = ({ text }) => (
  <div className="text-sm break-words">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
      {text}
    </ReactMarkdown>
  </div>
);

MarkdownMessage.propTypes = {
  text: PropTypes.string.isRequired,
};

export default MarkdownMessage;
